import type { IImprintData, IPromoBlock } from "../types";

/**
 * Stale-while-revalidate fetcher for the imprint's commitment.json + promos.json + privacy.json.
 * Per Studio Policy: only fetches from documented imprint endpoints; bundled fallback if offline.
 *
 * Popphin Learning games → popphin.com/_data/*
 * Billy Bisson (kids) games → billybisson.com/_data/* (mirrored from popphin.com at build time)
 */
export interface PromoFetcherConfig {
  commitmentUrl: string;
  promosUrl: string;
  privacyUrl: string;
  bundledFallback: IImprintData;
}

export class PromoFetcher {
  private cache: IImprintData | null = null;
  private config: PromoFetcherConfig | null = null;

  configure(config: PromoFetcherConfig): void {
    this.config = config;
    this.cache = config.bundledFallback;
  }

  current(): IImprintData {
    if (!this.cache) throw new Error("PromoFetcher not configured");
    return this.cache;
  }

  async revalidate(): Promise<void> {
    if (!this.config) return;
    const [commitment, promos, privacy] = await Promise.allSettled([
      fetchJson<{ text: string; lastUpdated: string; changelogUrl: string }>(this.config.commitmentUrl),
      fetchJson<IPromoBlock[]>(this.config.promosUrl),
      fetchJson<{ text: string; lastUpdated: string }>(this.config.privacyUrl)
    ]);
    const next: IImprintData = {
      commitment:
        commitment.status === "fulfilled" && commitment.value
          ? commitment.value
          : this.config.bundledFallback.commitment,
      promos:
        promos.status === "fulfilled" && promos.value
          ? promos.value
          : this.config.bundledFallback.promos,
      privacy:
        privacy.status === "fulfilled" && privacy.value
          ? privacy.value
          : this.config.bundledFallback.privacy
    };
    this.cache = next;
  }
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}
